import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import { Router, ActivatedRoute, RouterLink } from '@angular/router';
import { PasswordService } from '../../../core/services/password.service';

@Component({
    selector: 'app-reset-password',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, RouterLink],
    templateUrl: './reset-password.component.html',
    styleUrls: ['./reset-password.component.scss']
})
export class ResetPasswordComponent {
    private fb = inject(FormBuilder);
    private passwordService = inject(PasswordService);
    private router = inject(Router);
    private route = inject(ActivatedRoute);

    resetForm = this.fb.group({
        password: ['', [
            Validators.required,
            Validators.minLength(8),
            Validators.pattern(/(?=.*[a-z])/), // At least one lowercase
            Validators.pattern(/(?=.*[A-Z])/), // At least one uppercase
            Validators.pattern(/(?=.*\d)/),    // At least one number
            Validators.pattern(/(?=.*[!@#$%^&*])/), // At least one special char
        ]],
        confirmPassword: ['', Validators.required]
    });

    isLoading = false;
    errorMessage = '';
    successMessage = '';
    token = '';

    constructor() {
        // Get token from URL query params
        this.route.queryParams.subscribe(params => {
            this.token = params['token'] || '';
        });
    }

    onSubmit() {
        if (this.resetForm.valid) {
            if (this.resetForm.value.password !== this.resetForm.value.confirmPassword) {
                this.errorMessage = 'Passwords do not match';
                return;
            }

            if (!this.token) {
                this.errorMessage = 'Invalid or missing reset token.';
                return;
            }

            this.isLoading = true;
            this.errorMessage = '';
            this.passwordService.resetPassword({
                resetToken: this.token,
                newPassword: this.resetForm.value.password!
            }).subscribe({
                next: (response) => {
                    this.isLoading = false;
                    this.successMessage = 'Success! Your password has been reset.';
                    // Redirect to login after 5 seconds
                    setTimeout(() => {
                        this.router.navigate(['/login']);
                    }, 5000);
                },
                error: (err) => {
                    this.isLoading = false;
                    this.errorMessage = err.error?.message || 'Failed to reset password. Token may be expired or invalid.';
                }
            });
        }
    }

    get f() { return this.resetForm.controls; }

    get passwordValue() {
        return this.resetForm.get('password')?.value || '';
    }

    get hasMinLength() { return this.passwordValue.length >= 8; }
    get hasUpperCase() { return /[A-Z]/.test(this.passwordValue); }
    get hasLowerCase() { return /[a-z]/.test(this.passwordValue); }
    get hasNumber() { return /\d/.test(this.passwordValue); }
    get hasSpecialChar() { return /[!@#$%^&*]/.test(this.passwordValue); }
}
